import {
  ENUM_LABELS,
  type CreateReagentItemInput,
  type Department,
  type Supplier,
  toOptions,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { idChoice, nameOptions } from '#client/lib/form-choices';
import type { SetField } from '#client/hooks/useFormValues';

const UNIT_OPTIONS = toOptions(ENUM_LABELS.stockUnit);

interface FieldsProps {
  values: CreateReagentItemInput;
  onFieldChange: SetField<CreateReagentItemInput>;
  errors: Record<string, string>;
}

interface IdentityCardProps extends FieldsProps {
  departments: readonly Department[];
  suppliers: readonly Supplier[];
}

/** What the reagent is and where it lives: the "Reagent" card. */
export function ReagentIdentityCard({
  values,
  onFieldChange,
  errors,
  departments,
  suppliers,
}: IdentityCardProps) {
  return (
    <Card title="Reagent">
      <div className="c-form-grid">
        <TextField
          label="Name"
          value={values.name}
          error={errors.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          helpText="As it appears on the box"
          required
        />
        <TextField
          label="Code"
          value={values.code}
          error={errors.code}
          onChange={(event) => onFieldChange('code', event.target.value)}
          helpText="Short and unique, e.g. ALP-R1"
          required
        />
        <TextField
          label="Barcode"
          value={values.barcode ?? ''}
          error={errors.barcode}
          onChange={(event) => onFieldChange('barcode', event.target.value)}
        />
        <Select
          label="Department"
          options={nameOptions(departments)}
          placeholder="Choose a bench"
          value={idChoice(values.department_id)}
          error={errors.department_id}
          onChange={(choice) => onFieldChange('department_id', Number(choice))}
          required
        />
        <Select
          label="Usual supplier"
          options={nameOptions(suppliers)}
          placeholder="None"
          value={idChoice(values.default_supplier_id)}
          error={errors.default_supplier_id}
          onChange={(choice) =>
            onFieldChange('default_supplier_id', choice === '' ? null : Number(choice))
          }
        />
        <TextField
          label="Storage"
          value={values.storage_location ?? ''}
          error={errors.storage_location}
          onChange={(event) => onFieldChange('storage_location', event.target.value)}
          helpText="e.g. Fridge 2, top shelf"
        />
      </div>

      <Checkbox
        label="Active"
        checked={values.is_active}
        onChange={(event) => onFieldChange('is_active', event.target.checked)}
      />
    </Card>
  );
}

interface PackCardProps extends FieldsProps {
  /** Stock has moved, so the unit and pack size are frozen. */
  hasMoved: boolean;
}

/** How the reagent is counted and how much arrives in one pack. */
export function ReagentPackCard({ values, onFieldChange, errors, hasMoved }: PackCardProps) {
  const unitLabel = ENUM_LABELS.stockUnit[values.base_unit];

  return (
    <Card
      title="Unit and pack"
      subtitle="How this reagent is counted, and how much arrives in one pack"
    >
      {hasMoved && (
        <Banner tone="info">
          Stock has already moved for this reagent, so the unit and pack size are fixed — changing
          them would rewrite what every past receipt meant. Create a new reagent if the vendor has
          changed the pack.
        </Banner>
      )}

      <div className="c-form-grid">
        <Select
          label="Counted in"
          options={UNIT_OPTIONS}
          value={values.base_unit}
          error={errors.base_unit}
          onChange={(choice) => onFieldChange('base_unit', choice)}
          disabled={hasMoved}
          helpText="Every quantity for this reagent is in this unit"
          required
        />
        <NumberField
          label="Pack size"
          min={0.0001}
          step="any"
          value={values.pack_size_base}
          error={errors.pack_size_base}
          onChange={(size) => onFieldChange('pack_size_base', size)}
          disabled={hasMoved}
          suffix={unitLabel}
          helpText="A 50 mL bottle counted in µL is 50000"
          required
        />
        <TextField
          label="Pack description"
          value={values.pack_label}
          error={errors.pack_label}
          onChange={(event) => onFieldChange('pack_label', event.target.value)}
          helpText="e.g. 1 × 50 mL bottle"
          required
        />
        <NumberField
          label="Reorder at"
          min={0}
          step="any"
          value={values.reorder_level_base}
          error={errors.reorder_level_base}
          onChange={(level) => onFieldChange('reorder_level_base', level)}
          suffix={unitLabel}
          helpText="Falling to or below this shows a reorder notice. 0 turns it off."
        />
      </div>

      <TextArea
        label="Note"
        value={values.note ?? ''}
        error={errors.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
        rows={2}
      />
    </Card>
  );
}
